import {
  ldsOurPriceContextDisplayNis,
  ldsOurTotalFareDisplayNis,
  sumRoomsLdsDisplayNis,
} from "./ldsVacationPackageDisplayPrice";
import { resolvePublicRoomPricingSnapshot } from "./publicRoomPricingSSOT";
import { resolvePublicDisplayPrice } from "./publicDisplayPriceSSOT";
import {
  applyPublicCommission,
  resolveCategoryCommission,
} from "./publicSitePricingCommission";
import { logPublicCommissionFlowTrace } from "./publicVacationPackageDisplayPrice";
import { hrDebugTrace, isHrDebugPricing } from "./hrDebugFlags";
import { applyAgentHotelLayerToIlsAmount } from "./publicAgentLayeredPricing";
import { sumSelectedRoomsFinalPriceIls } from "./vacationPackageRoomPricingSSOT";
import {
  extractPublicVacationPackageSnapshotId,
  extractPublicVacationPackageCustomerPrice,
} from "./publicVacationPackageSnapshotId";
import { computeCanonicalSearchCardDisplayedPrice } from "./lds/computeCanonicalSearchCardDisplayedPrice";

function resolveOurTotalFareSourceField(ourTotalFare) {
  const tf = ourTotalFare && typeof ourTotalFare === "object" ? ourTotalFare : null;
  if (!tf) return { sourceFieldUsed: "none", sourceValue: 0 };
  const withCc = Number(tf.priceWithCommissionAndCreditCardFee);
  if (Number.isFinite(withCc) && withCc > 0) {
    return {
      sourceFieldUsed: "ourTotalFare.priceWithCommissionAndCreditCardFee",
      sourceValue: withCc,
    };
  }
  const withCommission = Number(tf.priceWithCommission);
  if (Number.isFinite(withCommission) && withCommission > 0) {
    return {
      sourceFieldUsed: "ourTotalFare.priceWithCommission",
      sourceValue: withCommission,
    };
  }
  const withCard = Number(tf.priceWithCreditCardFee);
  if (Number.isFinite(withCard) && withCard > 0) {
    return {
      sourceFieldUsed: "ourTotalFare.priceWithCreditCardFee",
      sourceValue: withCard,
    };
  }
  const initial = Number(tf.initialPrice);
  if (Number.isFinite(initial) && initial > 0) {
    return {
      sourceFieldUsed: "ourTotalFare.initialPrice",
      sourceValue: initial,
    };
  }
  return { sourceFieldUsed: "none", sourceValue: 0 };
}

function pickFirstMeaningfulId(candidates) {
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const normalized = String(candidate).trim();
    if (normalized !== "") return normalized;
  }
  return null;
}

function emitPublicPackageRehydrationTrace(payload) {
  try {
    hrDebugTrace("[PUBLIC_PACKAGE_REHYDRATION_TRACE]", payload);
  } catch (_) {
    /* ignore */
  }
}

function isPublicVacationPackageSourceTag(sourceTag) {
  const tag = String(sourceTag || "");
  return (
    tag.startsWith("VacationPackagesResults.") ||
    tag.startsWith("VacationPackagesCategories.") ||
    tag.startsWith("RandomPackages.") ||
    tag.startsWith("MiniSiteHomeFeaturedSection.") ||
    tag.startsWith("VacationPackagesDiscoveryRails.") ||
    tag.startsWith("toPackageViewModel") ||
    tag.startsWith("VpInlineBookingStickyBar.") ||
    tag.startsWith("VpPackageConversionLayout.") ||
    tag.startsWith("VacationPackagesOrderSummary.") ||
    tag.startsWith("VacationPackagesBooking.") ||
    tag.startsWith("RoomsSelector.")
  );
}

export function resolveVacationPackageAgentFinalPriceFromAgentFlow({
  selectedResult,
  extendedDeal = null,
  shortenedDeal = null,
  occupancyRooms = null,
  calculateLDSDealFareForRoomsComposition,
}) {
  const fullDeal = selectedResult?.fullDeal || selectedResult || null;
  if (!fullDeal || typeof calculateLDSDealFareForRoomsComposition !== "function") {
    return {
      agentFinalPrice: null,
      sourcePriceField: "missing_calculateLDSDealFareForRoomsComposition",
      forbiddenSourceUsed: true,
    };
  }
  const agentDisplayedPrice = resolveAgentPackageDisplayedPrice({
    fullDeal,
    extendedDeal: extendedDeal || selectedResult?.extendedDeal || null,
    shortenedDeal: shortenedDeal || selectedResult?.shortenedDeal || null,
    occupancyRooms:
      occupancyRooms ??
      selectedResult?.searchOccupancyRooms ??
      selectedResult?.fullDeal?.searchOccupancyRooms ??
      null,
    calculateLDSDealFareForRoomsComposition,
  });
  if (Number.isFinite(agentDisplayedPrice) && agentDisplayedPrice > 0) {
    return {
      agentFinalPrice: agentDisplayedPrice,
      sourcePriceField:
        "calculateLDSDealFareForRoomsComposition.ourTotalFare.priceWithCommissionAndCreditCardFee",
      forbiddenSourceUsed: false,
    };
  }
  return {
    agentFinalPrice: null,
    sourcePriceField: "calculateLDSDealFareForRoomsComposition.ourTotalFare.priceWithCommissionAndCreditCardFee",
    forbiddenSourceUsed: true,
  };
}

export function resolveAgentPackageDisplayedPrice({
  fullDeal,
  extendedDeal = null,
  shortenedDeal = null,
  occupancyRooms = null,
  calculateLDSDealFareForRoomsComposition,
  /** When true (e.g. DEV package-tier preview), recompute from live entitlements — skip frozen server snapshot. */
  ignorePricingSnapshot = false,
}) {
  return computeCanonicalSearchCardDisplayedPrice({
    fullDeal,
    extendedDeal,
    shortenedDeal,
    occupancyRooms,
    calculateLDSDealFareForRoomsComposition,
    ignorePricingSnapshot,
  });
}

export function resolvePublicPackageBasePriceToAgent({
  fullDeal,
  extendedDeal = null,
  shortenedDeal = null,
  calculateLDSDealFareForRoomsComposition,
}) {
  const tryFare = (fare) => {
    const otf = fare?.ourTotalFare && typeof fare.ourTotalFare === "object" ? fare.ourTotalFare : fare;
    const supplierNet = Number(otf?.initialPrice);
    const hrCommission = Number(otf?.hrCommission);
    if (
      Number.isFinite(supplierNet) &&
      supplierNet > 0 &&
      Number.isFinite(hrCommission) &&
      hrCommission >= 0
    ) {
      return supplierNet + hrCommission;
    }
    return null;
  };
  if (fullDeal && typeof calculateLDSDealFareForRoomsComposition === "function") {
    try {
      const fare = calculateLDSDealFareForRoomsComposition(
        fullDeal,
        extendedDeal || shortenedDeal || null
      );
      const fromCalc = tryFare(fare);
      if (fromCalc != null) return fromCalc;
    } catch {
      /* fall through to deal snapshot */
    }
  }
  if (fullDeal && typeof fullDeal === "object") {
    const fromDeal = tryFare(fullDeal) ?? tryFare(fullDeal.ourPrice);
    if (fromDeal != null) return fromDeal;
  }
  return null;
}

export function resolveMergedPublicSitePricingSettings(publicSitePricingSettings) {
  if (publicSitePricingSettings && typeof publicSitePricingSettings === "object") {
    return publicSitePricingSettings;
  }
  if (
    typeof window !== "undefined" &&
    window.__HR_PUBLIC_SITE_PRICING_SETTINGS &&
    typeof window.__HR_PUBLIC_SITE_PRICING_SETTINGS === "object"
  ) {
    return window.__HR_PUBLIC_SITE_PRICING_SETTINGS;
  }
  return null;
}

/**
 * אובייקט selectedResult ברמת השורה — כמו toSelectedResultPayload — עבור computeVacationPackageCheckoutPricingNis.
 */
export function vacationPackageRowToSelectedResultStub(rawRow) {
  if (rawRow == null) return null;
  const row = rawRow?.fullDeal ? rawRow : { fullDeal: rawRow, shortenedDeal: null };
  const root = row.fullDeal || row;
  const snapshotId =
    extractPublicVacationPackageSnapshotId(rawRow) || extractPublicVacationPackageSnapshotId(root);
  const publicPrice =
    extractPublicVacationPackageCustomerPrice(rawRow) ??
    extractPublicVacationPackageCustomerPrice(root);
  return {
    ...root,
    ...(snapshotId ? { pricing_snapshot_id: snapshotId } : {}),
    ...(publicPrice != null ? { public_customer_price: publicPrice } : {}),
  };
}

/**
 * לוג אחיד לכרטיסי רשימה ציבוריים — מאותה משפחת נתונים כמו computeVacationPackageCheckoutPricingNis (לא לפי "צבע").
 * הופעלה רק עם localStorage DEBUG_PUBLIC_CARD_PRICING=true (נמנע הצפה ברינדור מאות כרטיסים).
 */
export function emitPublicVacationPackageListCardPricingProof({
  surface,
  pricingSourceTag = "unknown",
  selectedResultStub,
  calculateLDSDealFareForRoomsComposition,
  checkout,
}) {
  const debugOn =
    typeof localStorage !== "undefined" &&
    localStorage.getItem("DEBUG_PUBLIC_CARD_PRICING") === "true";
  if (!debugOn) return;
  let agentFlow = null;
  try {
    agentFlow = resolveVacationPackageAgentFinalPriceFromAgentFlow({
      selectedResult: selectedResultStub,
      calculateLDSDealFareForRoomsComposition,
    });
  } catch {
    agentFlow = null;
  }
  const forbidden = Boolean(agentFlow?.forbiddenSourceUsed);
  const totalOk =
    checkout?.totalNis != null &&
    Number.isFinite(Number(checkout.totalNis)) &&
    Number(checkout.totalNis) > 0 &&
    !checkout.pricingBlockedReason;
  const ssotOk = !forbidden && totalOk;
  try {
    console.log("[PUBLIC_CARD_SURFACE]", surface);
    console.log("[PRICE_SOURCE]", {
      pricingSourceTag,
      agentPriceField: agentFlow?.sourcePriceField ?? null,
      checkoutPath: "computeVacationPackageCheckoutPricingNis.public_unified_or_legacy",
      blockedReason: checkout?.pricingBlockedReason ?? null,
    });
    console.log("[SSOT_VALIDATION]", ssotOk, {
      engineSsotField: "ourTotalFare.priceWithCommissionAndCreditCardFee",
      forbiddenAgentSource: forbidden,
    });
  } catch {
    /* ignore */
  }
}

function hasExplicitVacationPackageCommissionInSettings(settings) {
  if (!settings || typeof settings !== "object") return false;
  const n = settings.vacation_package;
  if (n && typeof n === "object" && !Array.isArray(n)) {
    if (
      Object.prototype.hasOwnProperty.call(n, "type") ||
      Object.prototype.hasOwnProperty.call(n, "value")
    ) {
      return true;
    }
  }
  const t = settings.public_vacation_package_commission_type;
  const v = settings.public_vacation_package_commission_value;
  if (t != null && String(t).trim() !== "") return true;
  if (v != null && String(v).trim() !== "") return true;
  if (
    settings.public_site_fixed_vacation_package_commission != null &&
    String(settings.public_site_fixed_vacation_package_commission).trim() !== "" &&
    Number(settings.public_site_fixed_vacation_package_commission) > 0
  ) {
    return true;
  }
  return false;
}

/**
 * מחיר ILS_SSOT סופי: אם יש הגדרות ציבוריות טעונות — מחשבים מחדש משכבת הסוכן (לפני עמלת אתר)
 * ולא משתמשים ב-finalIls שבסנאפשוט (עשוי לכלול עמלה ישנה / 10% מ-sessionStorage).
 */
export function resolvePublicFinalIlsFromSsotSnapshot(
  publicPackagePriceSnapshot,
  publicSitePricingSettings = null
) {
  const snap = publicPackagePriceSnapshot;
  if (!snap || snap.pricingModel !== "ILS_SSOT") {
    const f = Number(snap?.finalIls);
    return Number.isFinite(f) && f > 0 ? f : null;
  }
  const finalSnap = Number(snap.finalIls);
  if (!Number.isFinite(finalSnap) || finalSnap <= 0) return null;

  const pricingSettings = resolveMergedPublicSitePricingSettings(publicSitePricingSettings);
  const baseBefore = Number(snap.baseIlsBeforePublicSiteCommission);
  if (
    pricingSettings != null &&
    typeof pricingSettings === "object" &&
    Number.isFinite(baseBefore) &&
    baseBefore > 0
  ) {
    const publicCommission = resolveCategoryCommission(pricingSettings, "vacation_package");
    return applyPublicCommission(baseBefore, publicCommission, {
      agentDisplayedPrice: baseBefore,
      commissionCategory: "vacation_package",
      sourceSettings: pricingSettings,
      sourceFunction: "resolvePublicFinalIlsFromSsotSnapshot",
      hypothesisId: "H_ssot_rehydrate_live_settings",
    });
  }
  return finalSnap;
}

/**
 * מחירי תצוגה בזרימת checkout חבילות B2C — מקור אמת יחיד לסיכום צד, סרגל דביק ו־VacationPackagesBooking.
 */
export function computeVacationPackageCheckoutPricingNis({
  selectedResult,
  roomsAndPaxInfo,
  ourPrice,
  ldsSupplement,
  calculateLDSDealFareForRoomsComposition,
  publicSitePricingSettings = null,
  publicPackagePriceSnapshot = null,
  pricingSourceTag = "unknown",
  agentHotelPricing = null,
  selectedPublicRoomPriceSnapshot = null,
}) {
  const pricingSettings = resolveMergedPublicSitePricingSettings(publicSitePricingSettings);
  const publicSiteUnifiedPricing =
    pricingSettings != null && typeof pricingSettings === "object";
  const sourceTag = String(pricingSourceTag || "unknown");
  const isPublicSource = isPublicVacationPackageSourceTag(sourceTag);
  const supplementArray = ldsSupplement ? Object.values(ldsSupplement) : [];
  const checkedSupplements = supplementArray.filter((supp) => supp.checked);
  const supplementsTotal = checkedSupplements.reduce(
    (sum, supp) => sum + (supp.ourFare || 0),
    0
  );

  const serverPublicPrice = Number(
    selectedResult?.public_customer_price ??
      selectedResult?.fullDeal?.public_customer_price
  );
  if (Number.isFinite(serverPublicPrice) && serverPublicPrice > 0) {
    return {
      packagePrice: serverPublicPrice,
      supplementsTotal,
      totalNis: serverPublicPrice + supplementsTotal,
    };
  }

  if (!publicSiteUnifiedPricing && isPublicSource) {
    return {
      packagePrice: null,
      supplementsTotal,
      totalNis: null,
      pricingBlockedReason: "waiting_for_public_pricing_settings",
    };
  }

  if (publicSiteUnifiedPricing) {
    const fullDeal =
      selectedResult?.fullDeal && typeof selectedResult.fullDeal === "object"
        ? selectedResult.fullDeal
        : selectedResult && typeof selectedResult === "object"
          ? selectedResult
          : null;
    if (!fullDeal || Object.keys(fullDeal).length === 0) {
      emitPublicPackageRehydrationTrace({
        location: sourceTag,
        packageId:
          selectedResult?.dealId ??
          selectedResult?.ldsDealId ??
          selectedResult?.fullDeal?.dealId ??
          null,
        hasFullDeal: false,
        hasExtendedDeal: false,
        hasShortenedDeal: Boolean(selectedResult?.shortenedDeal),
        shortenedDealLength: Array.isArray(selectedResult?.shortenedDeal)
          ? selectedResult.shortenedDeal.length
          : selectedResult?.shortenedDeal && typeof selectedResult.shortenedDeal === "object"
            ? Object.keys(selectedResult.shortenedDeal).length
            : 0,
        selectedRoomId: pickFirstMeaningfulId([
          selectedResult?.selectedRoom?.roomId,
          selectedResult?.selectedRoom?.RoomId,
          selectedResult?.selectedRoom?.room_id,
          selectedResult?.selectedRoom?.roomCode,
          selectedResult?.selectedRoom?.id,
          selectedResult?.selectedRoom?.["@seqNum"],
        ]),
        roomInfoSignature: null,
        agentFinalPrice: null,
        publicFinalPrice: null,
        source: "computeVacationPackageCheckoutPricingNis",
        status: "waiting_for_package_deal_data",
      });
      return {
        packagePrice: null,
        supplementsTotal,
        totalNis: null,
        pricingBlockedReason: "waiting_for_package_deal_data",
      };
    }
    const roomsSnapshotPackagePrice = isPublicSource
      ? null
      : sumSelectedRoomsFinalPriceIls(roomsAndPaxInfo, {
      publicSitePricingSettings: pricingSettings,
      agentHotelPricing,
      selectedResult,
      board: fullDeal?.hotel?.board ?? null,
      packageId:
        selectedResult?.dealId ??
        selectedResult?.ldsDealId ??
        fullDeal?.dealId ??
        null,
      hotelId: fullDeal?.hotel?.hotelId ?? fullDeal?.hotel?.HotelId ?? null,
    });
    if (roomsSnapshotPackagePrice != null && roomsSnapshotPackagePrice > 0) {
      return {
        packagePrice: roomsSnapshotPackagePrice,
        supplementsTotal,
        totalNis: roomsSnapshotPackagePrice + supplementsTotal,
      };
    }
    const agentFinalPrice = isPublicSource
      ? resolvePublicPackageBasePriceToAgent({
          fullDeal,
          extendedDeal: selectedResult?.extendedDeal || fullDeal?.extended || null,
          shortenedDeal: selectedResult?.shortenedDeal || null,
          calculateLDSDealFareForRoomsComposition,
        })
      : Number(
          resolveVacationPackageAgentFinalPriceFromAgentFlow({
            selectedResult,
            calculateLDSDealFareForRoomsComposition,
          })?.agentFinalPrice
        );
    const ssot = resolvePublicDisplayPrice({
      productType: "vacation_package",
      agentFinalPrice: Number.isFinite(agentFinalPrice) && agentFinalPrice > 0 ? agentFinalPrice : null,
      publicSitePricingSettings: pricingSettings,
      quantity: 1,
      currency: "ILS",
      context: {
        isPublicSiteActive: true,
        isAgentPro: true,
        location: sourceTag,
      },
    });
    const publicFinalBase = Number(ssot?.publicFinalPrice);
    const totalNis =
      Number.isFinite(publicFinalBase) && publicFinalBase > 0
        ? publicFinalBase + supplementsTotal
        : null;
    emitPublicPackageRehydrationTrace({
      location: sourceTag,
      packageId:
        selectedResult?.dealId ??
        selectedResult?.ldsDealId ??
        selectedResult?.fullDeal?.dealId ??
        null,
      hasFullDeal: Boolean(fullDeal),
      hasExtendedDeal: Boolean(fullDeal?.extended ?? selectedResult?.extended),
      hasShortenedDeal: Boolean(selectedResult?.shortenedDeal),
      shortenedDealLength: Array.isArray(selectedResult?.shortenedDeal)
        ? selectedResult.shortenedDeal.length
        : selectedResult?.shortenedDeal && typeof selectedResult.shortenedDeal === "object"
          ? Object.keys(selectedResult.shortenedDeal).length
          : 0,
      selectedRoomId: pickFirstMeaningfulId([
        selectedResult?.selectedRoom?.roomId,
        selectedResult?.selectedRoom?.RoomId,
        selectedResult?.selectedRoom?.room_id,
        selectedResult?.selectedRoom?.roomCode,
        selectedResult?.selectedRoom?.id,
        selectedResult?.selectedRoom?.["@seqNum"],
      ]),
      roomInfoSignature: null,
      agentFinalPrice:
        Number.isFinite(agentFinalPrice) && agentFinalPrice > 0 ? agentFinalPrice : null,
      publicFinalPrice:
        Number.isFinite(publicFinalBase) && publicFinalBase > 0 ? publicFinalBase : null,
      source: "computeVacationPackageCheckoutPricingNis",
      status: ssot?.status ?? "invalid_agent_price",
    });
    if (ssot?.status !== "ready") {
      return {
        packagePrice: null,
        supplementsTotal,
        totalNis: null,
        pricingBlockedReason: ssot?.status || "invalid_agent_price",
      };
    }
    return {
      packagePrice: publicFinalBase,
      supplementsTotal,
      totalNis,
    };
  }

  const isIlsSsot =
    publicPackagePriceSnapshot?.pricingModel === "ILS_SSOT" &&
    publicPackagePriceSnapshot?.finalIls != null &&
    Number.isFinite(Number(publicPackagePriceSnapshot.finalIls)) &&
    Number(publicPackagePriceSnapshot.finalIls) > 0;

  const allowSnapshotSource =
    !sourceTag.startsWith("VacationPackagesBooking.") &&
    !sourceTag.startsWith("VacationPackagesOrderSummary.") &&
    !sourceTag.startsWith("RoomsSelector.") &&
    !sourceTag.startsWith("VpPackageConversionLayout.");
  if (isIlsSsot && !publicSiteUnifiedPricing) {
    const rawSnapshotFinal = Number(publicPackagePriceSnapshot.finalIls);
    const agentBaseBeforePublic = Number(
      publicPackagePriceSnapshot.baseIlsBeforePublicSiteCommission
    );
    const publicCommissionLive = resolveCategoryCommission(pricingSettings, "vacation_package");
    const publicPackagePrice = resolvePublicFinalIlsFromSsotSnapshot(
      publicPackagePriceSnapshot,
      publicSitePricingSettings
    );
    const rehydratedFromSettings =
      pricingSettings != null &&
      Number.isFinite(agentBaseBeforePublic) &&
      agentBaseBeforePublic > 0;
    const totalNis =
      publicPackagePrice != null && Number.isFinite(Number(publicPackagePrice))
        ? Number(publicPackagePrice) + supplementsTotal
        : null;
    try {
      logPublicCommissionFlowTrace({
        component: `computeVacationPackageCheckoutPricingNis:${sourceTag}`,
        baseIls: publicPackagePriceSnapshot.baseIlsBeforePublicSiteCommission ?? null,
        publicCommission: publicCommissionLive,
        finalIls: totalNis,
        usdDisplay: null,
        source: rehydratedFromSettings
          ? "ILS_SSOT_rehydrate_live_settings+supplements"
          : "ILS_SSOT_snapshot_final+supplements",
      });
    } catch {
      /* ignore */
    }
    return {
      packagePrice: publicPackagePrice,
      supplementsTotal,
      totalNis,
    };
  }

  const packagePriceFromContext = ldsOurPriceContextDisplayNis(ourPrice);
  const packagePriceFromRooms = Array.isArray(roomsAndPaxInfo)
    ? sumRoomsLdsDisplayNis(roomsAndPaxInfo)
    : 0;
  const opFromResult = selectedResult?.ourPrice || selectedResult?.ourprice;
  const packagePriceFromResult = ldsOurTotalFareDisplayNis(opFromResult?.ourTotalFare);

  let packagePriceFromDealCompute = 0;
  let dealComputeSourceFieldUsed = "none";
  let dealComputeRawFields = null;
  try {
    const fullDeal = selectedResult?.fullDeal || selectedResult;
    if (fullDeal && calculateLDSDealFareForRoomsComposition) {
      const fare = calculateLDSDealFareForRoomsComposition(
        fullDeal,
        selectedResult?.shortenedDeal
      );
      const tf = fare?.ourTotalFare ?? null;
      const src = resolveOurTotalFareSourceField(tf);
      dealComputeSourceFieldUsed = src.sourceFieldUsed;
      dealComputeRawFields = tf
        ? {
            priceWithCommissionAndCreditCardFee: tf.priceWithCommissionAndCreditCardFee ?? null,
            priceWithCommission: tf.priceWithCommission ?? null,
            priceWithCreditCardFee: tf.priceWithCreditCardFee ?? null,
            initialPrice: tf.initialPrice ?? null,
            finalPriceGBP: tf.finalPriceGBP ?? null,
            originalCurrency: tf.originalCurrency ?? null,
          }
        : null;
      const v = ldsOurTotalFareDisplayNis(fare?.ourTotalFare);
      if (v > 0) packagePriceFromDealCompute = v;
    }
  } catch {
    /* ignore */
  }

  const packagePriceFromSnapshot =
    !publicSiteUnifiedPricing &&
    allowSnapshotSource &&
    publicPackagePriceSnapshot?.finalIls != null &&
    Number.isFinite(Number(publicPackagePriceSnapshot.finalIls)) &&
    Number(publicPackagePriceSnapshot.finalIls) > 0
      ? Number(publicPackagePriceSnapshot.finalIls)
      : 0;
  const roomSelectionDrivenSource =
    sourceTag.startsWith("VacationPackagesBooking.") ||
    sourceTag.startsWith("VacationPackagesOrderSummary.") ||
    sourceTag.startsWith("VpInlineBookingStickyBar.") ||
    sourceTag.startsWith("VpPackageConversionLayout.") ||
    sourceTag.startsWith("RoomsSelector.");
  const selectedRoomFromResult =
    selectedResult?.selectedRoom ??
    selectedResult?.fullDeal?.selectedRoom ??
    null;
  const selectedRoomFromPax =
    Array.isArray(roomsAndPaxInfo) && roomsAndPaxInfo.length > 0
      ? roomsAndPaxInfo[0]?.room ?? null
      : null;
  const selectedRoomForPublic = selectedRoomFromResult || selectedRoomFromPax;
  const selectedRoomIdForPublic =
    pickFirstMeaningfulId([
      selectedRoomForPublic?.roomId,
      selectedRoomForPublic?.RoomId,
      selectedRoomForPublic?.room_id,
      selectedRoomForPublic?.id,
      selectedRoomForPublic?.roomCode,
      selectedRoomForPublic?.RoomCode,
      selectedRoomForPublic?.["@seqNum"],
    ]) ??
    pickFirstMeaningfulId([
      selectedRoomFromPax?.roomId,
      selectedRoomFromPax?.RoomId,
      selectedRoomFromPax?.room_id,
      selectedRoomFromPax?.id,
      selectedRoomFromPax?.roomCode,
      selectedRoomFromPax?.RoomCode,
      selectedRoomFromPax?.["@seqNum"],
    ]);
  const inlineSelectedRoomSnapshot =
    publicSiteUnifiedPricing &&
    roomSelectionDrivenSource &&
    selectedPublicRoomPriceSnapshot == null &&
    selectedRoomForPublic &&
    selectedRoomIdForPublic != null
      ? resolvePublicRoomPricingSnapshot({
          selectedRoomId: selectedRoomIdForPublic,
          room: selectedRoomForPublic,
          agentRoomFinalPrice: ldsOurTotalFareDisplayNis(
            selectedRoomForPublic?.ourFareDetails?.ourTotalFare
          ),
          publicSitePricingSettings: pricingSettings,
          productType: "vacation_package_room",
          packageId:
            selectedResult?.dealId ??
            selectedResult?.ldsDealId ??
            selectedResult?.fullDeal?.dealId ??
            null,
          hotelId: selectedResult?.hotel?.hotelId ?? selectedResult?.hotel?.HotelId ?? null,
          board: selectedResult?.hotel?.board ?? null,
          occupancySignature: null,
          currency: "ILS",
          context: {
            isPublicSiteActive: true,
            isAgentPro: true,
            hasAgentHotelPricing: Boolean(agentHotelPricing),
          },
        })
      : null;
  const effectiveSelectedPublicRoomPriceSnapshot =
    selectedPublicRoomPriceSnapshot ?? inlineSelectedRoomSnapshot;
  const shouldEnforceSelectedRoomSnapshot =
    publicSiteUnifiedPricing &&
    roomSelectionDrivenSource &&
    (selectedRoomIdForPublic != null || effectiveSelectedPublicRoomPriceSnapshot != null);
  const selectedRoomSnapshotReady =
    effectiveSelectedPublicRoomPriceSnapshot &&
    effectiveSelectedPublicRoomPriceSnapshot.status === "ready" &&
    Number.isFinite(Number(effectiveSelectedPublicRoomPriceSnapshot.publicFinalPrice)) &&
    Number(effectiveSelectedPublicRoomPriceSnapshot.publicFinalPrice) > 0;
  const selectedRoomSnapshotMatchesSelection =
    selectedRoomSnapshotReady &&
    selectedRoomIdForPublic != null &&
    effectiveSelectedPublicRoomPriceSnapshot.roomId != null &&
    String(effectiveSelectedPublicRoomPriceSnapshot.roomId) === String(selectedRoomIdForPublic);
  if (shouldEnforceSelectedRoomSnapshot && !selectedRoomSnapshotReady) {
    return {
      packagePrice: null,
      supplementsTotal,
      totalNis: null,
      pricingBlockedReason:
        effectiveSelectedPublicRoomPriceSnapshot?.status ||
        "waiting_for_selected_public_room_price_snapshot",
    };
  }
  if (shouldEnforceSelectedRoomSnapshot && selectedRoomSnapshotReady && !selectedRoomSnapshotMatchesSelection) {
    return {
      packagePrice: null,
      supplementsTotal,
      totalNis: null,
      pricingBlockedReason: "selected_room_snapshot_mismatch",
    };
  }
  let packagePrice =
    publicSiteUnifiedPricing && roomSelectionDrivenSource && selectedRoomSnapshotMatchesSelection
      ? Number(effectiveSelectedPublicRoomPriceSnapshot.publicFinalPrice)
      : publicSiteUnifiedPricing && packagePriceFromDealCompute > 0
      ? packagePriceFromDealCompute
      : packagePriceFromSnapshot > 0
        ? packagePriceFromSnapshot
        : packagePriceFromContext > 0
          ? packagePriceFromContext
          : packagePriceFromRooms > 0
            ? packagePriceFromRooms
            : packagePriceFromResult > 0
              ? packagePriceFromResult
              : packagePriceFromDealCompute;
  const priceSourceKind =
    publicSiteUnifiedPricing &&
    roomSelectionDrivenSource &&
    selectedRoomSnapshotMatchesSelection &&
    packagePrice === Number(effectiveSelectedPublicRoomPriceSnapshot.publicFinalPrice)
      ? "selected_public_room_price_snapshot"
      : publicSiteUnifiedPricing && packagePriceFromDealCompute > 0 && packagePrice === packagePriceFromDealCompute
      ? "deal_compute"
      : packagePriceFromSnapshot > 0
        ? "snapshot"
        : packagePriceFromContext > 0
          ? "context_ourPrice"
          : packagePriceFromRooms > 0
            ? "rooms_sum"
            : packagePriceFromResult > 0
              ? "selectedResult_fare"
              : packagePriceFromDealCompute > 0
                ? "deal_compute_fallback"
                : "none";
  const baseAlreadyIncludesAgentLayer =
    priceSourceKind === "selected_public_room_price_snapshot" ||
    priceSourceKind === "deal_compute" ||
    priceSourceKind === "deal_compute_fallback" ||
    priceSourceKind === "context_ourPrice" ||
    priceSourceKind === "rooms_sum" ||
    priceSourceKind === "selectedResult_fare";
  const publicCommission = resolveCategoryCommission(pricingSettings, "vacation_package");
  const shouldSkipCommission =
    priceSourceKind === "selected_public_room_price_snapshot" ||
    packagePriceFromSnapshot > 0 && publicPackagePriceSnapshot?.includesPublicCommission === true;

  let prePublicPackageNis = packagePrice;
  if (packagePrice > 0 && !shouldSkipCommission && !baseAlreadyIncludesAgentLayer) {
    const { agentFinalPrice, pricingBlocked } = applyAgentHotelLayerToIlsAmount({
      ilsAmount: packagePrice,
      agentHotelPricing,
      suppressTrace: true,
      sourceFunction: `computeVacationPackageCheckoutPricingNis:${sourceTag}`,
      packageId:
        selectedResult?.dealId ??
        selectedResult?.ldsDealId ??
        selectedResult?.fullDeal?.dealId ??
        null,
      hotelId: selectedResult?.hotel?.hotelId ?? selectedResult?.hotel?.HotelId ?? null,
      roomId:
        selectedResult?.selectedRoom?.roomId ?? selectedResult?.selectedRoom?.RoomId ?? null,
    });
    if (pricingBlocked || agentFinalPrice == null || !Number.isFinite(agentFinalPrice)) {
      return {
        packagePrice: null,
        supplementsTotal,
        totalNis: null,
        pricingBlockedReason: "missing_agent_hotel_pricing",
      };
    }
    prePublicPackageNis = agentFinalPrice;
  }

  const publicPackagePrice =
    packagePrice > 0
      ? shouldSkipCommission
        ? packagePrice
        : applyPublicCommission(prePublicPackageNis, publicCommission, {
            agentDisplayedPrice: prePublicPackageNis,
            commissionCategory: "vacation_package",
            sourceSettings: pricingSettings,
            sourceFunction: `computeVacationPackageCheckoutPricingNis:${sourceTag}`,
            hypothesisId: "H_checkout_summary_compute",
          })
      : packagePrice;
  if (isHrDebugPricing()) {
    hrDebugTrace("[PUBLIC_PACKAGE_PRICE_SNAPSHOT]", {
      source: packagePriceFromSnapshot > 0 ? "checkout-render-snapshot" : "checkout-render",
      packageId:
        selectedResult?.dealId ??
        selectedResult?.ldsDealId ??
        selectedResult?.fullDeal?.dealId ??
        null,
      siteId:
        (typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("siteId")
          : null) || null,
      rawBaseUsd: null,
      rawBaseIls: packagePrice,
      prePublicBaseIlsAfterHr: prePublicPackageNis,
      finalUsd: null,
      finalIls:
        publicPackagePrice != null && Number.isFinite(Number(publicPackagePrice))
          ? Number(publicPackagePrice) + supplementsTotal
          : null,
      commissionType: publicCommission?.type ?? null,
      commissionValue: Number(publicCommission?.value) || 0,
      commissionPercent:
        publicCommission?.type === "percent" ? Number(publicCommission?.value) || 0 : null,
      commissionApplied: publicPackagePrice !== prePublicPackageNis,
      includesPublicCommission: true,
      selectedRoomId:
        selectedResult?.selectedRoom?.roomId ?? selectedResult?.selectedRoom?.RoomId ?? null,
      selectedFlightId:
        selectedResult?.linkedFlights?.ldsDealId ??
        selectedResult?.linkedFlights?.dealId ??
        null,
      paxCount: Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo.length : null,
      nights: Number(selectedResult?.duration?.nights) || null,
      currencyRate: null,
      fromContext: packagePriceFromContext,
      fromRooms: packagePriceFromRooms,
      fromResult: packagePriceFromResult,
      fromDealCompute: packagePriceFromDealCompute,
      fromSnapshot: packagePriceFromSnapshot,
      skipCommissionBecauseSnapshot: shouldSkipCommission,
      pricingSourceTag,
      allowSnapshotSource,
      hasPublicSitePricingSettings: Boolean(pricingSettings),
    });
  }

  return {
    packagePrice: publicPackagePrice,
    supplementsTotal,
    totalNis:
      publicPackagePrice != null && Number.isFinite(Number(publicPackagePrice))
        ? Number(publicPackagePrice) + supplementsTotal
        : null,
  };
}
